import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { interestsApi, Interest, InterestCategory } from "@/api/user/interests";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface InterestsListProps {
  userId: number;
}

const categoryLabels: Record<InterestCategory, string> = {
  hobbies: "Hobbies",
  sport: "Sports",
  art: "Art",
  music: "Music",
  movie: "Movies",
  book: "Books",
  tv_show: "TV Shows",
  other: "Other"
};

const categoryIcons: Record<InterestCategory, string> = {
  hobbies: "🎨",
  sport: "⚽",
  art: "🖼️",
  music: "🎵",
  movie: "🎬",
  book: "📚",
  tv_show: "📺",
  other: "✨"
};

export function InterestsList({ userId }: InterestsListProps) {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingInterest, setEditingInterest] = useState<Interest | null>(null);
  const [category, setCategory] = useState<InterestCategory>("hobbies");
  const [detail, setDetail] = useState("");

  const { data: interests = [], isLoading } = useQuery({
    queryKey: ['interests', userId],
    queryFn: () => interestsApi.getInterests(userId),
    enabled: !!userId,
  });

  const createMutation = useMutation({
    mutationFn: (data: { category: InterestCategory; detail: string }) =>
      interestsApi.createInterest(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interests', userId] });
      toast.success("Interest added successfully");
      handleCloseDialog();
    },
    onError: () => {
      toast.error("Failed to add interest");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { category: InterestCategory; detail: string } }) =>
      interestsApi.updateInterest(userId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interests', userId] });
      toast.success("Interest updated successfully");
      handleCloseDialog();
    },
    onError: () => {
      toast.error("Failed to update interest");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => interestsApi.deleteInterest(userId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interests', userId] });
      toast.success("Interest deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete interest");
    },
  });

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingInterest(null);
    setCategory("hobbies");
    setDetail("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!detail.trim()) return;

    if (editingInterest) {
      updateMutation.mutate({
        id: editingInterest.id,
        data: { category, detail: detail.trim() }
      });
    } else {
      createMutation.mutate({ category, detail: detail.trim() });
    }
  };

  const handleEdit = (interest: Interest) => {
    setEditingInterest(interest);
    setCategory(interest.category);
    setDetail(interest.detail);
    setIsDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm("Are you sure you want to delete this interest?")) {
      deleteMutation.mutate(id);
    }
  };

  // Group interests by category
  const groupedInterests = interests.reduce((acc, interest) => {
    if (!acc[interest.category]) {
      acc[interest.category] = [];
    }
    acc[interest.category].push(interest);
    return acc;
  }, {} as Record<InterestCategory, Interest[]>);

  if (isLoading) {
    return (
      <Card className="p-6 shadow-lg border-border/50 hover:shadow-xl transition-all">
        <h3 className="text-lg font-semibold mb-4 text-primary">Interests</h3>
        <p className="text-sm text-muted-foreground">Loading...</p>
      </Card>
    );
  }

  return (
    <Card className="p-6 shadow-lg border-border/50 hover:shadow-xl transition-all">
      <h3 className="text-lg font-semibold mb-4 text-primary">Interests</h3>
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={() => setEditingInterest(null)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Interest
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingInterest ? "Edit Interest" : "Add Interest"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={category} onValueChange={(value) => setCategory(value as InterestCategory)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(categoryLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {categoryIcons[key as InterestCategory]} {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="detail">Detail</Label>
                  <Input
                    id="detail"
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                    placeholder="e.g., Playing guitar, Reading sci-fi novels..."
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={handleCloseDialog}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                    {editingInterest ? "Update" : "Add"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        {interests.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No interests added yet. Add your interests to help friends find the perfect gifts for you!
          </p>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedInterests).map(([cat, items]) => (
              <div key={cat} className="space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <span>{categoryIcons[cat as InterestCategory]}</span>
                  {categoryLabels[cat as InterestCategory]}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {items.map((interest) => (
                    <div
                      key={interest.id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm"
                    >
                      <span>{interest.detail}</span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEdit(interest)}
                          className="hover:bg-primary/20 rounded p-0.5"
                        >
                          <Edit className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => handleDelete(interest.id)}
                          className="hover:bg-red-500/20 rounded p-0.5 text-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

